import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CafeCard from "@/components/CafeCard";
import StarRating from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Coffee, Heart } from "lucide-react";

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [favCafes, setFavCafes] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [tab, setTab] = useState("favorites");

  useEffect(() => {
    if (user === false) navigate("/login", { state: { from: "/profile" } });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [{ data: favs }, { data: reviews }] = await Promise.all([
          api.get("/favorites"),
          api.get("/users/me/reviews"),
        ]);
        setFavCafes(favs.cafes || []);
        setMyReviews(reviews);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [user]);

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="profile-page">
      <div className="border border-border bg-card rounded-sm p-8 md:p-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="flex items-center gap-5">
          <span className="h-16 w-16 rounded-full bg-[#C05A46] text-[#F9F6F0] grid place-items-center text-2xl font-serif">
            {user.name?.[0]?.toUpperCase()}
          </span>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Your account</p>
            <h1 className="font-serif text-4xl mt-2">{user.name}</h1>
            <p className="text-sm text-[#786B63]">{user.email}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button asChild className="rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0]">
            <Link to="/submit" data-testid="profile-submit-cafe">Submit a café</Link>
          </Button>
          <Button onClick={async () => { await logout(); navigate("/"); }} variant="outline" className="rounded-sm">
            Sign out
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-10 border-b border-border flex gap-8">
        <button
          onClick={() => setTab("favorites")}
          data-testid="tab-favorites"
          className={`pb-3 -mb-px text-sm tracking-wide uppercase ${tab === "favorites" ? "text-[#C05A46] border-b-2 border-[#C05A46]" : "text-[#786B63]"}`}
        >
          Favorites · {favCafes.length}
        </button>
        <button
          onClick={() => setTab("reviews")}
          data-testid="tab-reviews"
          className={`pb-3 -mb-px text-sm tracking-wide uppercase ${tab === "reviews" ? "text-[#C05A46] border-b-2 border-[#C05A46]" : "text-[#786B63]"}`}
        >
          My reviews · {myReviews.length}
        </button>
      </div>

      <div className="mt-10">
        {tab === "favorites" ? (
          favCafes.length === 0 ? (
            <div className="text-center py-16 border border-border rounded-sm bg-card">
              <Heart className="h-7 w-7 text-[#C05A46] mx-auto" strokeWidth={1.5} />
              <p className="font-serif text-2xl mt-4">No favorites yet</p>
              <p className="text-sm text-[#786B63] mt-2">Tap the heart on any café to save it here.</p>
              <Button asChild className="mt-5 rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0]">
                <Link to="/directory">Explore the directory</Link>
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {favCafes.map((c) => <CafeCard key={c.id} cafe={c} />)}
            </div>
          )
        ) : (
          myReviews.length === 0 ? (
            <div className="text-center py-16 border border-border rounded-sm bg-card">
              <Coffee className="h-7 w-7 text-[#C05A46] mx-auto" strokeWidth={1.5} />
              <p className="font-serif text-2xl mt-4">No reviews yet</p>
              <p className="text-sm text-[#786B63] mt-2">Visited somewhere great? Leave a review.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {myReviews.map((r) => (
                <Link
                  key={r.id}
                  to={`/cafe/${r.cafe_id}`}
                  data-testid={`my-review-${r.id}`}
                  className="block border border-border bg-card rounded-sm p-6 hover:-translate-y-0.5 transition-transform"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-2xl">{r.cafe_name}</h3>
                    <StarRating rating={r.rating} />
                  </div>
                  <p className="text-sm text-[#2C1E16]/90 mt-3 leading-relaxed">{r.comment}</p>
                  <p className="text-xs text-[#786B63] mt-3">{new Date(r.created_at).toLocaleDateString()}</p>
                </Link>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
